import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Avatar,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Button
} from '@mui/material';
import { 
  TwoWheeler, DirectionsBike, Person, 
  CheckCircle, Warning, Build, HighlightOff,
  Refresh
} from '@mui/icons-material';
import { green, blue, orange, deepPurple, red, grey } from '@mui/material/colors';
import { getDashboardStats } from '../services/dashboardService';
import { useAuth } from '../services/auth/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Dashboard = () => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, logout } = useAuth();
  const [shouldFetch, setShouldFetch] = useState(true); // Add this state

  useEffect(() => {
    // Only fetch if shouldFetch is true and we have a user
    if (shouldFetch && user) {
      const fetchData = async () => {
        try {
          setLoading(true);
          const result = await getDashboardStats(user.role);
          
          if (result.success) {
            setStatsData(result.data);
            setShouldFetch(false); // Stop further fetches after success
          } else {
            setError(result.message);
            // Only logout on clear authentication failures, not general API errors
            if (result.message?.includes('No authentication token found')) {
              console.warn("No auth token, logging out");
              logout();
            }
          }
        } catch (err) {
          console.error('Dashboard error:', err);
          
          // Don't logout on network errors, just show error
          if (err.message?.includes('Network Error') || err.message?.includes('timeout')) {
            setError('Connection error - please check your internet connection');
          } else {
            setError('An error occurred while loading dashboard data');
          }
          setShouldFetch(false); // Stop on error
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [user, shouldFetch, logout]); 

  // Stats configuration with role-based filtering
  const statsConfig = [
    { title: 'Total Scooters', value: statsData?.totalScooters, icon: <TwoWheeler />, color: deepPurple[500] },
    { title: 'Available Scooters', value: statsData?.availableScooters, icon: <CheckCircle />, color: green[500] },
    { title: 'Rented Scooters', value: statsData?.rentedScooters, icon: <DirectionsBike />, color: blue[500] },
    { title: 'In Maintenance', value: statsData?.inMaintenance, icon: <Build />, color: orange[500] },
    { title: 'Unassembled Scooters', value: statsData?.disassembleScooters, icon: <HighlightOff />, color: grey[700] },
    { title: 'Total Riders', value: statsData?.totalRiders, icon: <Person />, color: deepPurple[500] },
    ...(user?.role === 'admin' ? [
      { title: 'Total Student Employees', value: statsData?.totalStudentEmployees, icon: <Person />, color: green[500] }
    ] : []),
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      
      <Container maxWidth="lg" sx={{ py: 15, flex: 1 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ 
            fontWeight: 'bold',
            color: deepPurple[800],
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <DirectionsBike fontSize="large" />
            {user?.role === 'admin' ? 'Admin Dashboard' : 'Employee Dashboard'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" color="text.secondary">
              Last updated: {new Date().toLocaleString()}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setShouldFetch(true);
                setError(null);
              }}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={8}>
          {statsConfig.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper elevation={3} sx={{ 
                p: 3,
                borderRadius: 2,
                height: '100%',
                borderLeft: `4px solid ${stat.color}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: `${stat.color}20`,
                    color: stat.color,
                    width: 48,
                    height: 48
                  }}>
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" color="text.secondary">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {stat.value ?? 0}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Snackbar
        open={!!error}
        autoHideDuration={2500}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Footer />
    </Box>
  );
};

export default Dashboard;